"use client";

import { useActionState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils/cn";

import { subscribeToRelaunch, type RelaunchSignupState } from "./subscribe-action";

/**
 * The site-wide "paused for maintenance" band, shown above the header on the
 * consumer shop and the company site while `ORDERING_PAUSED` is on. Carries
 * its own copy rather than next-intl messages because the company tree sits
 * outside next-intl (see `src/app/foretag/layout.tsx`).
 *
 * Static markup plus a client form — safe inside the configurator's PPR
 * prelude and the prerendered `/foretag/konfigurera`.
 */

const COPY = {
  sv: {
    eyebrow: "Tillfälligt stängt",
    heading: "Vi har pausat butiken för underhåll",
    body: "Just nu kan vi inte ta emot några beställningar. Lämna din mejladress så hör vi av oss så fort det går att beställa igen.",
    label: "Din mejladress",
    placeholder: "namn@exempel.se",
    submit: "Meddela mig",
    pending: "Sparar …",
    success: "Tack! Vi mejlar dig när vi är tillbaka.",
    invalid_email: "Det där ser inte ut som en mejladress.",
    error: "Något gick snett. Försök igen om en stund.",
    privacy: "Vi använder adressen bara till det här mejlet.",
  },
  en: {
    eyebrow: "Temporarily closed",
    heading: "We've paused the shop for maintenance",
    body: "We can't take any orders right now. Leave your email and we'll let you know as soon as you can order again.",
    label: "Your email",
    placeholder: "name@example.com",
    submit: "Notify me",
    pending: "Saving …",
    success: "Thanks! We'll email you when we're back.",
    invalid_email: "That doesn't look like an email address.",
    error: "Something went wrong. Please try again in a moment.",
    privacy: "We'll only use your address for that one email.",
  },
} as const;

interface OrderingPausedBannerProps {
  locale: "sv" | "en";
  source: "consumer" | "company";
}

const INITIAL_STATE: RelaunchSignupState = { status: "idle" };

export function OrderingPausedBanner({ locale, source }: OrderingPausedBannerProps) {
  const copy = COPY[locale];
  const [state, formAction, isPending] = useActionState(subscribeToRelaunch, INITIAL_STATE);
  const message =
    state.status === "idle" ? null : copy[state.status];

  return (
    <section
      aria-labelledby="ordering-paused-heading"
      className="border-b-4 border-cobalt bg-ink text-paper"
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 py-6 sm:px-6 sm:py-8 md:flex-row md:items-center md:justify-between md:gap-10">
        <div className="flex max-w-2xl flex-col gap-2">
          <p className="font-mono text-xs font-medium uppercase tracking-[0.14em] text-peach">
            {copy.eyebrow}
          </p>
          <h2
            id="ordering-paused-heading"
            className="font-heading text-3xl leading-tight sm:text-4xl"
          >
            {copy.heading}
          </h2>
          <p className="text-base text-paper/85 sm:text-lg">{copy.body}</p>
        </div>

        {state.status === "success" ? (
          <p role="status" className="text-lg font-medium md:max-w-xs">
            {copy.success}
          </p>
        ) : (
          <form action={formAction} className="flex w-full flex-col gap-2 md:max-w-sm">
            <label htmlFor="relaunch-email" className="text-sm font-medium">
              {copy.label}
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="relaunch-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                defaultValue={state.email}
                placeholder={copy.placeholder}
                aria-invalid={state.status === "invalid_email" || undefined}
                aria-describedby={message ? "relaunch-message" : undefined}
                className="h-11 border-paper/40 bg-paper text-ink placeholder:text-ink-3"
              />
              <Button
                type="submit"
                size="lg"
                disabled={isPending}
                className="h-11"
              >
                {isPending ? copy.pending : copy.submit}
              </Button>
            </div>
            {/* Honeypot — see `subscribeToRelaunch`. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden
              className="hidden"
            />
            <input type="hidden" name="source" value={source} />
            <input type="hidden" name="locale" value={locale} />
            <p
              id="relaunch-message"
              role={message ? "alert" : undefined}
              className={cn("text-sm", message ? "text-rose" : "text-paper/70")}
            >
              {message ?? copy.privacy}
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
